import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch,
  faTimes,
  faChevronRight,
  faChevronLeft,
} from '@fortawesome/free-solid-svg-icons';
import MediaModal from '../components/MediaModal'; // <-- unified modal
import ActorModal from '../components/ActorModal';

const TMDB_API_KEY = "57240db50c2008e78c261e1a934627e4";

// ========== TRANSLATIONS ==========
const translations = {
  en: {
    filtersLabel: 'FILTERS',
    categoryLabel: 'Category',
    searchActorsLabel: 'Search Actors',
    searchPlaceholder: 'Type actor name...',
    searchButton: 'Search Actors',
    loadingActors: 'Loading actors...',
    noResults: 'No actors found for "{term}"',
    back: 'Back',
    next: 'Next',
    pageOf: 'Page {current} of {total}',
    actorNamed: 'Actor named {name}',
    categories: {
      genZ: 'Gen Z Stars',
      modernGreats: 'Modern Greats',
      screenLegends: 'Screen Legends',
      popular: 'Popular Now',
    },
  },
  fr: {
    filtersLabel: 'FILTRES',
    categoryLabel: 'Catégorie',
    searchActorsLabel: 'Rechercher des acteurs',
    searchPlaceholder: 'Tapez un nom d\'acteur...',
    searchButton: 'Rechercher des acteurs',
    loadingActors: 'Chargement des acteurs...',
    noResults: 'Aucun acteur trouvé pour "{term}"',
    back: 'Retour',
    next: 'Suivant',
    pageOf: 'Page {current} sur {total}',
    actorNamed: 'Acteur nommé {name}',
    categories: {
      genZ: 'Stars Gen Z',
      modernGreats: 'Grands Modernes',
      screenLegends: 'Légendes de l\'écran',
      popular: 'Populaire maintenant',
    },
  },
  es: {
    filtersLabel: 'FILTROS',
    categoryLabel: 'Categoría',
    searchActorsLabel: 'Buscar actores',
    searchPlaceholder: 'Escriba el nombre del actor...',
    searchButton: 'Buscar actores',
    loadingActors: 'Cargando actores...',
    noResults: 'No se encontraron actores para "{term}"',
    back: 'Atrás',
    next: 'Siguiente',
    pageOf: 'Página {current} de {total}',
    actorNamed: 'Actor llamado {name}',
    categories: {
      genZ: 'Estrellas Gen Z',
      modernGreats: 'Grandes Modernos',
      screenLegends: 'Leyendas de la pantalla',
      popular: 'Popular ahora',
    },
  },
  it: {
    filtersLabel: 'FILTRI',
    categoryLabel: 'Categoria',
    searchActorsLabel: 'Cerca attori',
    searchPlaceholder: 'Digita il nome dell\'attore...',
    searchButton: 'Cerca attori',
    loadingActors: 'Caricamento attori...',
    noResults: 'Nessun attore trovato per "{term}"',
    back: 'Indietro',
    next: 'Avanti',
    pageOf: 'Pagina {current} di {total}',
    actorNamed: 'Attore chiamato {name}',
    categories: {
      genZ: 'Stelle Gen Z',
      modernGreats: 'Grandi Moderni',
      screenLegends: 'Leggende dello schermo',
      popular: 'Popolari ora',
    },
  },
  nl: {
    filtersLabel: 'FILTERS',
    categoryLabel: 'Categorie',
    searchActorsLabel: 'Zoek acteurs',
    searchPlaceholder: 'Typ een acteursnaam...',
    searchButton: 'Zoek acteurs',
    loadingActors: 'Acteurs laden...',
    noResults: 'Geen acteurs gevonden voor "{term}"',
    back: 'Terug',
    next: 'Volgende',
    pageOf: 'Pagina {current} van {total}',
    actorNamed: 'Acteur genaamd {name}',
    categories: {
      genZ: 'Gen Z Sterren',
      modernGreats: 'Moderne Groten',
      screenLegends: 'Schermlegendes',
      popular: 'Nu populair',
    },
  },
};

// Predefined backdrops for actors
const predefinedBackdrops = [
  "https://image.tmdb.org/t/p/w300/2DD6LbVEtsvh8doEKjxQEdOaBbB.jpg",
  "https://image.tmdb.org/t/p/w300/y7tjLYcq2ZGy2DNG0ODhGX9Tm60.jpg"
];

const getActorData = (name) => {
  const actorsMap = {
    "Bradley Cooper": { id: 51329, profile_path: "https://image.tmdb.org/t/p/w500/DPnessSsWqVXRbKm93PtMjB4Us.jpg" },
    "Cillian Murphy": { id: 2037, profile_path: "https://image.tmdb.org/t/p/w500/3DZAf9CwXmfV2HZOEdqeZAGTndV.jpg" },
    "Anya Taylor-Joy": { id: 1397778, profile_path: "https://image.tmdb.org/t/p/w500/gOSoqWg4zyO40DOWf9hQjzl3cBl.jpg" },
    "Jenna Ortega": { id: 974169, profile_path: "https://image.tmdb.org/t/p/w500/7oUAtVgZU0uLdUSvDHKkINt1y7Y.jpg" },
    "Keanu Reeves": { id: 6384, profile_path: "https://image.tmdb.org/t/p/w500/d9HyjGMCt4wgJIOxAGlaYWhKsiN.jpg" },
    "Liam Neeson": { id: 3896, profile_path: "https://image.tmdb.org/t/p/w500/jrf9LaTFWfLA5DBhFWENFsWh4Y9.jpg" },
    "Ana de Armas": { id: 224513, profile_path: "https://image.tmdb.org/t/p/w500/3vxvsmYLTf4jnr163SUlBIw51ee.jpg" },
    "Angelina Jolie": { id: 11701, profile_path: "https://image.tmdb.org/t/p/w500/bXNxIKcJ5cNNW8QFrBPWcfTSu9x.jpg" },
    "Dwayne Johnson": { id: 18918, profile_path: "https://image.tmdb.org/t/p/w500/5QApZVV8FUFlVxQpIK3Ew6cqotq.jpg" },
    "Kevin Hart": { id: 55638, profile_path: "https://image.tmdb.org/t/p/w500/7OwsFfoxNGIfWwSmdORyB7v8XNj.jpg" },
    "Anne Hathaway": { id: 1813, profile_path: "https://image.tmdb.org/t/p/w500/s6tflSD20MGz04ZR2R1lZvhmC4Y.jpg" },
    "Saoirse Ronan": { id: 36592, profile_path: "https://image.tmdb.org/t/p/w500/xcFNTQj2ShECqUe7pG6YiVMUw2g.jpg" },
    "Sylvester Stallone": { id: 16483, profile_path: "https://image.tmdb.org/t/p/w500/gn3pDWthJqR0VDYGViGD3048og7.jpg" },
    "Arnold Schwarzenegger": { id: 1100, profile_path: "https://image.tmdb.org/t/p/w500/2marNcjIfCUE5Z2HyrcEiJHPbsA.jpg" },
    "Joey King": { id: 125025, profile_path: "https://image.tmdb.org/t/p/w500/td1mTuzaajIzN9EjK5Bd6ly6WBO.jpg" },
    "Thomasin McKenzie": { id: 1356758, profile_path: "https://image.tmdb.org/t/p/w500/WOpnEFG5Q8LWxP81MtUrskmVox.jpg" },
    "Marlon Wayans": { id: 9562, profile_path: "https://image.tmdb.org/t/p/w500/r0Ol5exKLKYXEbVqLTHxX64GvIr.jpg" },
    "Shawn Wayans": { id: 35690, profile_path: "https://image.tmdb.org/t/p/w500/Cat25uXhB680QLmvg9Tu16W563.jpg" },
    "Jennifer Lawrence": { id: 72129, profile_path: "https://image.tmdb.org/t/p/w500/nApY80UBr58POBMKKxei3kHZR8D.jpg" },
    "Millie Bobby Brown": { id: 1356210, profile_path: "https://image.tmdb.org/t/p/w500/cQoPC18cHBN1zYTbOy5hbpjZ5ls.jpg" },
    "Ryan Reynolds": { id: 10859, profile_path: "https://image.tmdb.org/t/p/w500/6m3hkkHmKkD4MUJhng7ojE7MLKC.jpg" },
    "Zac Efron": { id: 29222, profile_path: "https://image.tmdb.org/t/p/w500/6yL3AGhT3QBQBY6SSWIFWwdQySl.jpg" },
    "Zendaya": { id: 505710, profile_path: "https://image.tmdb.org/t/p/w500/w1SXOfsLzo5heKLCKLIPGN9hDaf.jpg" },
    "Naomi Scott": { id: 240724, profile_path: "https://image.tmdb.org/t/p/w500/6OLe7TskbEvYpo36eITfX91VoCP.jpg" },
    "Vin Diesel": { id: 12835, profile_path: "https://image.tmdb.org/t/p/w500/vD28HWRqBxDgdGLXSgu6XYcn3BI.jpg" },
    "Jackie Chan": { id: 18897, profile_path: "https://image.tmdb.org/t/p/w500/nraZoTzwJQPHspAVsKfgl3RXKKa.jpg" },
    "Emma Stone": { id: 54693, profile_path: "https://image.tmdb.org/t/p/w500/eWjkPYeXxPhE2F9J3Ui6E9OWVXy.jpg" },
    "Regina Hall": { id: 35705, profile_path: "https://image.tmdb.org/t/p/w500/zY1IoUVTghMBNUnbqGzjN5AfjA6.jpg" },
    "Leonardo DiCaprio": { id: 6193, profile_path: "https://image.tmdb.org/t/p/w500/wo2hJpn04vbtmh0B9utCFdsQhxM.jpg" },
    "Johnny Depp": { id: 85, profile_path: "https://image.tmdb.org/t/p/w500/wcI594cwM4ArPwvRd2IU0Z0yLuh.jpg" },
    "Rami Malek": { id: 17838, profile_path: "https://image.tmdb.org/t/p/w500/zvBCjFmedqXRqa45jlLf6vBd9Nt.jpg" },
    "Daniel Craig": { id: 8784, profile_path: "https://image.tmdb.org/t/p/w500/ehDxfhad9BRpjNeOEuWznASlnSG.jpg" },
    "Sadie Sink": { id: 1590797, profile_path: "https://image.tmdb.org/t/p/w500/tEHNLdwPmxu3Ikkvq8I6FUfGdZL.jpg" },
    "Woody Harrelson": { id: 57755, profile_path: "https://image.tmdb.org/t/p/w500/nJgjLnkXcZuvsevbL3gvKk6TyDU.jpg" },
    "Jesse Eisenberg": { id: 44735, profile_path: "https://image.tmdb.org/t/p/w500/8BMnXLVJDysMO1E8JCAyOFv2QAb.jpg" },
    "Sadie Soverall": { id: 2270402, profile_path: "https://image.tmdb.org/t/p/w500/wGQgTJztCKwCZA79JgvZMQhhFFs.jpg" },
    "Maika Monroe": { id: 1094091, profile_path: "https://image.tmdb.org/t/p/w500/x4yiKkMnCdf8g4eOrDoqla9FYEA.jpg" },
    "Adam Sandler": { id: 19292, profile_path: "https://image.tmdb.org/t/p/w500/gLkLCsQdwDDPabRhKPB25nGtKwS.jpg" },
    "Jim Carrey": { id: 206, profile_path: "https://image.tmdb.org/t/p/w500/j4QY2GN2ZfObQbgedyV3Lihwrt.jpg" },
    "Victoria Pedretti": { id: 2117434, profile_path: "https://image.tmdb.org/t/p/w500/dSrIMNn803L8fqqWjkCSRv4iOu1.jpg" },
    "Samara Weaving": { id: 1372369, profile_path: "https://image.tmdb.org/t/p/w500/aMD5SVh0Zb0n5jOdbIfqbAjVNHd.jpg" },
    "Channing Tatum": { id: 38673, profile_path: "https://image.tmdb.org/t/p/w500/4TWmVfPdch0tvy1DLzVbof5HE7f.jpg" },
    "Jonah Hill": { id: 21007, profile_path: "https://image.tmdb.org/t/p/w500/cymlWttB83MsAGR2EkTgANtjeRH.jpg" },
    "Liana Liberato": { id: 60458, profile_path: "https://image.tmdb.org/t/p/w500/rAQq19psw1ZId2sm0wIHgD3L5yl.jpg" },
    "Scarlett Johansson": { id: 1245, profile_path: "https://image.tmdb.org/t/p/w500/zmvK1jJ6UZpAAeMMgdEOWir0kQN.jpg" },
    "Aaron Paul": { id: 84497, profile_path: "https://image.tmdb.org/t/p/w500/cuS6MzeZ6iy0RblUNGk5T2dGDnF.jpg" },
    "Bryan Cranston": { id: 17419, profile_path: "https://image.tmdb.org/t/p/w500/1lhMtNOYbMHPKa1ERly2HtcnKUj.jpg" },
    "Allison Williams": { id: 1255540, profile_path: "https://image.tmdb.org/t/p/w500/xMCiHNQxxRo5NEcKEjN9JE6bm9i.jpg" },
    "Madison Bailey": { id: 2232225, profile_path: "https://image.tmdb.org/t/p/w500/3jBI3nS68RIlCCnwEpnaSEz6e3n.jpg" },
    "Chris Hemsworth": { id: 74568, profile_path: "https://image.tmdb.org/t/p/w500/piQGdoIQOF3C1EI5cbYZLAW1gfj.jpg" },
    "Henry Cavill": { id: 73968, profile_path: "https://image.tmdb.org/t/p/w500/igcch9SlJkjxnKNK4lbkBifHt34.jpg" },
    "Margot Robbie": { id: 234352, profile_path: "https://image.tmdb.org/t/p/w500/4J18OMC2dQHmNNGy4q6KwK8QTe5.jpg" },
    "Kaitlyn Dever": { id: 481337, profile_path: "https://image.tmdb.org/t/p/w500/aIfYNhiQRsEa6V7riz7ul73Z5hq.jpg" },
    "Tom Holland": { id: 1136406, profile_path: "https://image.tmdb.org/t/p/w500/d4imT58b0w9YOK89fAtFbhNncoY.jpg" },
    "Timothée Chalamet": { id: 1190668, profile_path: "https://image.tmdb.org/t/p/w500/cc0kXVI82iosBRA7HoG7lVuUeRh.jpg" },
    "Sydney Sweeney": { id: 115440, profile_path: "https://image.tmdb.org/t/p/w500/yO356gXWSr6fgMw4UH1eYuhdOHI.jpg" },
    "Elizabeth Lail": { id: 1368507, profile_path: "https://image.tmdb.org/t/p/w500/lDYjEyqojUs2sF3CgdsOZ9vr2KL.jpg" },
    "Chris Evans": { id: 16828, profile_path: "https://image.tmdb.org/t/p/w500/a52ncG9JskJtqrryE0KakPPBkHJ.jpg" },
    "Jacob Elordi": { id: 2034418, profile_path: "https://image.tmdb.org/t/p/w500/lC9pK3ADyPpH4dRo2BKZBaTZPfu.jpg" },
    "Florence Pugh": { id: 1373737, profile_path: "https://image.tmdb.org/t/p/w500/tg4Pe5rikfGLPIHb3sp25uo4hnx.jpg" },
    "Madelyn Cline": { id: 1642789, profile_path: "https://image.tmdb.org/t/p/w500/qUUfTmpZPhy4zIs8oVk7OINLQqu.jpg" },
    "Pedro Pascal": { id: 1253360, profile_path: "https://image.tmdb.org/t/p/w500/9VYK7oxcqhjd5LAH6ZFJ3XzOlID.jpg" },
    "Paul Mescal": { id: 2326151, profile_path: "https://image.tmdb.org/t/p/w500/4GPZbpc0uoDKBWdpQchQXvle9tK.jpg" },
    "Hailee Steinfeld": { id: 130640, profile_path: "https://image.tmdb.org/t/p/w500/dxSDWkiVaC6JYjrV3XRAZI7HOSS.jpg" },
    "Hunter Schafer": { id: 2340180, profile_path: "https://image.tmdb.org/t/p/w500/wAYCnGgTLoq9oAmrXlJ0HmGF5kM.jpg" },
    "Harry Styles": { id: 1052109, profile_path: "https://image.tmdb.org/t/p/w500/84IKkPy5udiiMpXgYIC6Q1jB5WE.jpg" },
    "Dylan O'Brien": { id: 527393, profile_path: "https://image.tmdb.org/t/p/w500/xN3GdvIlqsR838gDoblhPH0numP.jpg" },
    "Emma Watson": { id: 10990, profile_path: "https://image.tmdb.org/t/p/w500/olWDsbbBUsPwmsJof4ceZEasRQ.jpg" },
    "Sophie Turner": { id: 1001657, profile_path: "https://image.tmdb.org/t/p/w500/fBgzfscYBaYOc4TUtmzlEnjkjZG.jpg" },
    "Morgan Freeman": { id: 192, profile_path: "https://image.tmdb.org/t/p/w500/jPsLqiYGSofU4s6BjrxnefMfabb.jpg" },
    "Robert De Niro": { id: 380, profile_path: "https://image.tmdb.org/t/p/w500/cT8htcckIuyI1Lqwt1CvD02ynTh.jpg" },
  };

  return actorsMap[name] || { id: name.toLowerCase().replace(/\s+/g, '-'), profile_path: "" };
};

const actorsByCategory = {
  genZ: [
    { id: getActorData("Jenna Ortega").id, name: "Jenna Ortega", imageUrl: getActorData("Jenna Ortega").profile_path },
    { id: getActorData("Joey King").id, name: "Joey King", imageUrl: getActorData("Joey King").profile_path },
    { id: getActorData("Thomasin McKenzie").id, name: "Thomasin McKenzie", imageUrl: getActorData("Thomasin McKenzie").profile_path },
    { id: getActorData("Millie Bobby Brown").id, name: "Millie Bobby Brown", imageUrl: getActorData("Millie Bobby Brown").profile_path },
    { id: getActorData("Zendaya").id, name: "Zendaya", imageUrl: getActorData("Zendaya").profile_path },
    { id: getActorData("Naomi Scott").id, name: "Naomi Scott", imageUrl: getActorData("Naomi Scott").profile_path },
    { id: getActorData("Sadie Sink").id, name: "Sadie Sink", imageUrl: getActorData("Sadie Sink").profile_path },
    { id: getActorData("Sadie Soverall").id, name: "Sadie Soverall", imageUrl: getActorData("Sadie Soverall").profile_path },
    { id: getActorData("Maika Monroe").id, name: "Maika Monroe", imageUrl: getActorData("Maika Monroe").profile_path },
    { id: getActorData("Victoria Pedretti").id, name: "Victoria Pedretti", imageUrl: getActorData("Victoria Pedretti").profile_path },
    { id: getActorData("Samara Weaving").id, name: "Samara Weaving", imageUrl: getActorData("Samara Weaving").profile_path },
    { id: getActorData("Liana Liberato").id, name: "Liana Liberato", imageUrl: getActorData("Liana Liberato").profile_path },
    { id: getActorData("Madison Bailey").id, name: "Madison Bailey", imageUrl: getActorData("Madison Bailey").profile_path },
    { id: getActorData("Kaitlyn Dever").id, name: "Kaitlyn Dever", imageUrl: getActorData("Kaitlyn Dever").profile_path },
    { id: getActorData("Tom Holland").id, name: "Tom Holland", imageUrl: getActorData("Tom Holland").profile_path },
    { id: getActorData("Timothée Chalamet").id, name: "Timothée Chalamet", imageUrl: getActorData("Timothée Chalamet").profile_path },
    { id: getActorData("Sydney Sweeney").id, name: "Sydney Sweeney", imageUrl: getActorData("Sydney Sweeney").profile_path },
    { id: getActorData("Elizabeth Lail").id, name: "Elizabeth Lail", imageUrl: getActorData("Elizabeth Lail").profile_path },
    { id: getActorData("Jacob Elordi").id, name: "Jacob Elordi", imageUrl: getActorData("Jacob Elordi").profile_path },
    { id: getActorData("Florence Pugh").id, name: "Florence Pugh", imageUrl: getActorData("Florence Pugh").profile_path },
    { id: getActorData("Madelyn Cline").id, name: "Madelyn Cline", imageUrl: getActorData("Madelyn Cline").profile_path },
    { id: getActorData("Paul Mescal").id, name: "Paul Mescal", imageUrl: getActorData("Paul Mescal").profile_path },
    { id: getActorData("Hailee Steinfeld").id, name: "Hailee Steinfeld", imageUrl: getActorData("Hailee Steinfeld").profile_path },
    { id: getActorData("Hunter Schafer").id, name: "Hunter Schafer", imageUrl: getActorData("Hunter Schafer").profile_path },
    { id: getActorData("Harry Styles").id, name: "Harry Styles", imageUrl: getActorData("Harry Styles").profile_path },
    { id: getActorData("Dylan O'Brien").id, name: "Dylan O'Brien", imageUrl: getActorData("Dylan O'Brien").profile_path },
    { id: getActorData("Emma Watson").id, name: "Emma Watson", imageUrl: getActorData("Emma Watson").profile_path },
    { id: getActorData("Sophie Turner").id, name: "Sophie Turner", imageUrl: getActorData("Sophie Turner").profile_path },
  ],
  modernGreats: [
    { id: getActorData("Anya Taylor-Joy").id, name: "Anya Taylor-Joy", imageUrl: getActorData("Anya Taylor-Joy").profile_path },
    { id: getActorData("Ana de Armas").id, name: "Ana de Armas", imageUrl: getActorData("Ana de Armas").profile_path },
    { id: getActorData("Saoirse Ronan").id, name: "Saoirse Ronan", imageUrl: getActorData("Saoirse Ronan").profile_path },
    { id: getActorData("Jennifer Lawrence").id, name: "Jennifer Lawrence", imageUrl: getActorData("Jennifer Lawrence").profile_path },
    { id: getActorData("Emma Stone").id, name: "Emma Stone", imageUrl: getActorData("Emma Stone").profile_path },
    { id: getActorData("Rami Malek").id, name: "Rami Malek", imageUrl: getActorData("Rami Malek").profile_path },
    { id: getActorData("Scarlett Johansson").id, name: "Scarlett Johansson", imageUrl: getActorData("Scarlett Johansson").profile_path },
    { id: getActorData("Chris Hemsworth").id, name: "Chris Hemsworth", imageUrl: getActorData("Chris Hemsworth").profile_path },
    { id: getActorData("Henry Cavill").id, name: "Henry Cavill", imageUrl: getActorData("Henry Cavill").profile_path },
    { id: getActorData("Margot Robbie").id, name: "Margot Robbie", imageUrl: getActorData("Margot Robbie").profile_path },
    { id: getActorData("Pedro Pascal").id, name: "Pedro Pascal", imageUrl: getActorData("Pedro Pascal").profile_path },
    { id: getActorData("Bradley Cooper").id, name: "Bradley Cooper", imageUrl: getActorData("Bradley Cooper").profile_path },
    { id: getActorData("Cillian Murphy").id, name: "Cillian Murphy", imageUrl: getActorData("Cillian Murphy").profile_path },
    { id: getActorData("Jonah Hill").id, name: "Jonah Hill", imageUrl: getActorData("Jonah Hill").profile_path },
    { id: getActorData("Anne Hathaway").id, name: "Anne Hathaway", imageUrl: getActorData("Anne Hathaway").profile_path },
    { id: getActorData("Ryan Reynolds").id, name: "Ryan Reynolds", imageUrl: getActorData("Ryan Reynolds").profile_path },
    { id: getActorData("Zac Efron").id, name: "Zac Efron", imageUrl: getActorData("Zac Efron").profile_path },
    { id: getActorData("Chris Evans").id, name: "Chris Evans", imageUrl: getActorData("Chris Evans").profile_path },
    { id: getActorData("Aaron Paul").id, name: "Aaron Paul", imageUrl: getActorData("Aaron Paul").profile_path },
    { id: getActorData("Allison Williams").id, name: "Allison Williams", imageUrl: getActorData("Allison Williams").profile_path },
    { id: getActorData("Woody Harrelson").id, name: "Woody Harrelson", imageUrl: getActorData("Woody Harrelson").profile_path },
    { id: getActorData("Jesse Eisenberg").id, name: "Jesse Eisenberg", imageUrl: getActorData("Jesse Eisenberg").profile_path },
  ],
  screenLegends: [
    { id: getActorData("Keanu Reeves").id, name: "Keanu Reeves", imageUrl: getActorData("Keanu Reeves").profile_path },
    { id: getActorData("Liam Neeson").id, name: "Liam Neeson", imageUrl: getActorData("Liam Neeson").profile_path },
    { id: getActorData("Angelina Jolie").id, name: "Angelina Jolie", imageUrl: getActorData("Angelina Jolie").profile_path },
    { id: getActorData("Dwayne Johnson").id, name: "Dwayne Johnson", imageUrl: getActorData("Dwayne Johnson").profile_path },
    { id: getActorData("Kevin Hart").id, name: "Kevin Hart", imageUrl: getActorData("Kevin Hart").profile_path },
    { id: getActorData("Sylvester Stallone").id, name: "Sylvester Stallone", imageUrl: getActorData("Sylvester Stallone").profile_path },
    { id: getActorData("Arnold Schwarzenegger").id, name: "Arnold Schwarzenegger", imageUrl: getActorData("Arnold Schwarzenegger").profile_path },
    { id: getActorData("Marlon Wayans").id, name: "Marlon Wayans", imageUrl: getActorData("Marlon Wayans").profile_path },
    { id: getActorData("Shawn Wayans").id, name: "Shawn Wayans", imageUrl: getActorData("Shawn Wayans").profile_path },
    { id: getActorData("Vin Diesel").id, name: "Vin Diesel", imageUrl: getActorData("Vin Diesel").profile_path },
    { id: getActorData("Daniel Craig").id, name: "Daniel Craig", imageUrl: getActorData("Daniel Craig").profile_path },
    { id: getActorData("Adam Sandler").id, name: "Adam Sandler", imageUrl: getActorData("Adam Sandler").profile_path },
    { id: getActorData("Bryan Cranston").id, name: "Bryan Cranston", imageUrl: getActorData("Bryan Cranston").profile_path },
    { id: getActorData("Channing Tatum").id, name: "Channing Tatum", imageUrl: getActorData("Channing Tatum").profile_path },
    { id: getActorData("Leonardo DiCaprio").id, name: "Leonardo DiCaprio", imageUrl: getActorData("Leonardo DiCaprio").profile_path },
    { id: getActorData("Johnny Depp").id, name: "Johnny Depp", imageUrl: getActorData("Johnny Depp").profile_path },
    { id: getActorData("Jim Carrey").id, name: "Jim Carrey", imageUrl: getActorData("Jim Carrey").profile_path },
    { id: getActorData("Morgan Freeman").id, name: "Morgan Freeman", imageUrl: getActorData("Morgan Freeman").profile_path },
    { id: getActorData("Robert De Niro").id, name: "Robert De Niro", imageUrl: getActorData("Robert De Niro").profile_path },
    { id: getActorData("Jackie Chan").id, name: "Jackie Chan", imageUrl: getActorData("Jackie Chan").profile_path },
  ],
};

// ========== ACTORS FILTER COMPONENT ==========
const ActorsFilter = ({ 
  currentCategory, 
  onCategoryChange,
  searchQuery,
  onSearchChange,
  onClearSearch,
  onSearchClick,
  loading,
  inputRef,
  lang
}) => {
  const t = (key) => {
    const langData = translations[lang] || translations.en;
    return langData[key] || key;
  };

  const tCategory = (catKey) => {
    const langData = translations[lang] || translations.en;
    return langData.categories?.[catKey] || catKey;
  };

  return (
    <div className="filters-container">
      <div className="filter-row">
        <div className="filter-group">
          <label className="filter-label" htmlFor="actorCategory">{t('categoryLabel')}</label>
          <select
            id="actorCategory"
            className="filter-select category-select"
            value={currentCategory}
            onChange={(e) => onCategoryChange(e.target.value)}
          >
            <option value="genZ">{tCategory('genZ')}</option>
            <option value="modernGreats">{tCategory('modernGreats')}</option>
            <option value="screenLegends">{tCategory('screenLegends')}</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label className="filter-label" htmlFor="actorSearch">{t('searchActorsLabel')}</label>
          <div className="search-input-container2">
            <input
              type="text"
              id="actorSearch"
              className="filter-select2 search-input"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              ref={inputRef}
              autoComplete="off"
            />
            {searchQuery && (
              <button className="clear-search-btn" onClick={onClearSearch}>
                &times;
              </button>
            )}
          </div>
        </div>
      </div>
      
      <div className="search-button-container">
        <button 
          className="search-actors-btn"
          onClick={onSearchClick}
          disabled={loading}
        >
          <FontAwesomeIcon icon={faSearch} />{t('searchButton')}
        </button>
      </div>
    </div>
  );
};

// ========== PAGINATION COMPONENT ==========
const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange,
  currentCategory,
  isSearchActive,
  lang
}) => {
  const t = (key) => {
    const langData = translations[lang] || translations.en;
    return langData[key] || key;
  };

  const displayTotalPages = (currentCategory === 'popular' && !isSearchActive) ? 
    "120" : 
    totalPages;

  return (
    <div className="pagination-container">
      <button 
        className="pagination-btn"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        <FontAwesomeIcon icon={faChevronLeft} /> {t('back')}
      </button>
      
      <div className="page-info">
        {t('pageOf').replace('{current}', currentPage).replace('{total}', displayTotalPages)}
      </div>
      
      <button 
        className="pagination-btn"
        disabled={currentCategory === 'popular' && !isSearchActive ? false : currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        {t('next')} <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
};

// ========== MAIN ACTORS PAGE ==========
const ActorsPage = () => {
  // ----- URL params -----
  const [searchParams, setSearchParams] = useSearchParams();

  // ----- Local state -----
  const storedCategory = localStorage.getItem('lastActorCategory') || 'modernGreats';
  const [currentCategory, setCurrentCategory] = useState(storedCategory);
  const [currentPage, setCurrentPage] = useState(1);
  const [popularActors, setPopularActors] = useState([]);
  const [totalPagesForPopular, setTotalPagesForPopular] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [totalPagesForSearch, setTotalPagesForSearch] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [selectedActor, setSelectedActor] = useState(null);
  // ----- Unified modal state -----
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaType, setSelectedMediaType] = useState(null);
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  
  const searchInputRef = useRef(null);
  const actorFallbackImage = 'https://i.ibb.co/Lkf9x3W/hsaSDasa.png';

  // ----- Language state -----
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('preferredLanguage') || 'en';
  });

  const t = (key) => {
    const langData = translations[lang] || translations.en;
    return langData[key] || key;
  };

  const tCategory = (catKey) => {
    const langData = translations[lang] || translations.en;
    return langData.categories?.[catKey] || catKey;
  };

  // ----- Listen to language changes -----
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'preferredLanguage') {
        setLang(e.newValue || 'en');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // ----- Body scroll lock -----
  useEffect(() => {
    if (selectedActor || selectedMedia) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedActor, selectedMedia]);

  // ----- Effect: sync URL params to state on mount and param changes -----
  useEffect(() => {
    const actorId = searchParams.get('actor');
    const movieId = searchParams.get('movie');
    const seriesId = searchParams.get('series');

    // If actor ID is in URL, find the actor in our local lists or fetch
    if (actorId && !selectedActor) {
      const actorIdNum = parseInt(actorId);
      // Search in all predefined lists and popular actors
      const allActors = [
        ...actorsByCategory.genZ,
        ...actorsByCategory.modernGreats,
        ...actorsByCategory.screenLegends,
        ...popularActors,
      ];
      const found = allActors.find(a => a.id === actorIdNum);
      if (found) {
        setSelectedActor(found);
      } else {
        // Fetch actor details by ID
        fetchActorDetails(actorIdNum).then(actor => {
          if (actor) {
            setSelectedActor(actor);
          }
        });
      }
    }

    // Open content modals if params exist
    if (movieId && !selectedMedia) {
      fetchMovieDetails(movieId).then(movie => {
        if (movie) {
          setSelectedMedia(movie);
          setSelectedMediaType('movie');
          setIsContentModalOpen(true);
        }
      });
    }
    if (seriesId && !selectedMedia) {
      fetchSeriesDetails(seriesId).then(series => {
        if (series) {
          setSelectedMedia(series);
          setSelectedMediaType('tv');
          setIsContentModalOpen(true);
        }
      });
    }
  }, [searchParams, popularActors]); // Re-run when popularActors changes

  // ----- Helper: fetch actor details from TMDB -----
  const fetchActorDetails = async (actorId) => {
    try {
      const response = await fetch(
        `https://api.themoviedb.org/3/person/${actorId}?api_key=${TMDB_API_KEY}`
      );
      const data = await response.json();
      return {
        id: data.id,
        name: data.name,
        imageUrl: data.profile_path ? `https://image.tmdb.org/t/p/w500${data.profile_path}` : actorFallbackImage,
      };
    } catch {
      return null;
    }
  };

  // ----- Helper: fetch movie details -----
  const fetchMovieDetails = async (movieId) => {
    try {
      const response = await fetch(
        `https://api.themoviedb.org/3/movie/${movieId}?api_key=${TMDB_API_KEY}&language=en-US`
      );
      const data = await response.json();
      return data;
    } catch {
      return null;
    }
  };

  // ----- Helper: fetch series details -----
  const fetchSeriesDetails = async (seriesId) => {
    try {
      const response = await fetch(
        `https://api.themoviedb.org/3/tv/${seriesId}?api_key=${TMDB_API_KEY}&language=en-US`
      );
      const data = await response.json();
      return data;
    } catch {
      return null;
    }
  };

  // ----- URL update helpers -----
  const setActorParam = (actorId) => {
    setSearchParams({ actor: actorId }, { replace: true });
  };

  const setContentParam = (actorId, mediaId, mediaType) => {
    const params = { actor: actorId };
    if (mediaType === 'movie') {
      params.movie = mediaId;
    } else {
      params.series = mediaId;
    }
    setSearchParams(params, { replace: true });
  };

  const clearContentParam = () => {
    const actorId = searchParams.get('actor');
    if (actorId) {
      setSearchParams({ actor: actorId }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const clearAllParams = () => {
    setSearchParams({}, { replace: true });
  };

  // ----- Modal open handlers -----
  const handleActorSelect = (actor) => {
    setSelectedActor(actor);
    setActorParam(actor.id);
    setHistory([]);
  };

  const handleContentClick = (content, type) => {
    const actorId = selectedActor?.id;
    if (!actorId) return;

    setSelectedMedia(content);
    setSelectedMediaType(type);
    setIsContentModalOpen(true);
    setContentParam(actorId, content.id, type);
    setHistory(prev => [...prev, { type: 'content', contentId: content.id }]);
  };

  // ----- Modal close handlers -----
  const handleActorModalClose = () => {
    setSelectedActor(null);
    clearAllParams();
  };

  const handleContentModalClose = () => {
    setIsContentModalOpen(false);
    setSelectedMedia(null);
    setSelectedMediaType(null);
    clearContentParam();
    setHistory([]);
  };

  // ----- Back handler (from content modal) -----
  const handleBackFromContent = () => {
    setIsContentModalOpen(false);
    setSelectedMedia(null);
    setSelectedMediaType(null);
    clearContentParam();
  };

  // ----- Existing fetch & search functions -----
  const getHighResImage = (url) => {
    if (!url) return actorFallbackImage;
    if (url.includes('image.tmdb.org')) {
      return url.replace(/\/(w500|original)\//, '/w300/');
    }
    return url;
  };

  const fetchPopularActors = async (page) => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://api.themoviedb.org/3/person/popular?api_key=${TMDB_API_KEY}&page=${page}`
      );
      const data = await response.json();
      
      const filteredResults = data.results.filter(actor => actor.profile_path);
      const actors = filteredResults.map(actor => ({
        id: actor.id,
        name: actor.name,
        imageUrl: `https://image.tmdb.org/t/p/w500${actor.profile_path}`
      }));
      
      setPopularActors(actors);
      setTotalPagesForPopular(data.total_pages);
    } catch (error) {
      console.error('Error fetching popular actors:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchActorsFromTMDB = async (query, page = 1) => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://api.themoviedb.org/3/search/person?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}&page=${page}`
      );
      const data = await response.json();
      
      const filteredResults = data.results.filter(actor => actor.profile_path);
      const actors = filteredResults.map(actor => ({
        id: actor.id,
        name: actor.name,
        imageUrl: `https://image.tmdb.org/t/p/w500${actor.profile_path}`
      }));
      
      setSearchResults(actors);
      setTotalPagesForSearch(data.total_pages);
    } catch (error) {
      console.error('Error searching actors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setCurrentCategory(category);
    localStorage.setItem('lastActorCategory', category);
    setFiltersCollapsed(true);
    setCurrentPage(1);
    setSearchQuery('');
    setSearchResults([]);
    setIsSearchActive(false);
    setSearchTerm('');
  };

  const handleSearchClick = () => {
    setFiltersCollapsed(true);
    
    if (searchQuery.trim()) {
      setCurrentPage(1);
      setSearchTerm(searchQuery);
      searchActorsFromTMDB(searchQuery);
      setIsSearchActive(true);
    } else {
      setIsSearchActive(false);
      setSearchResults([]);
      setSearchTerm('');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (currentCategory === 'popular' && !isSearchActive) {
      fetchPopularActors(currentPage);
    }
  }, [currentCategory, currentPage, isSearchActive]);

  useEffect(() => {
    if (isSearchActive && searchTerm && currentPage > 1) {
      searchActorsFromTMDB(searchTerm, currentPage);
    }
  }, [currentPage, isSearchActive, searchTerm]);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearchActive(false);
    setSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const getActorsForCategory = () => {
    if (isSearchActive) {
      return searchResults;
    }
    
    if (currentCategory === 'popular') {
      return popularActors;
    }
    
    const actors = actorsByCategory[currentCategory] || [];
    const startIndex = (currentPage - 1) * 20;
    return actors.slice(startIndex, startIndex + 20);
  };

  const getTotalPages = () => {
    if (isSearchActive) {
      return totalPagesForSearch;
    }
    
    if (currentCategory === 'popular') {
      return totalPagesForPopular;
    }
    
    const actors = actorsByCategory[currentCategory] || [];
    return Math.ceil(actors.length / 20);
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const actorsToDisplay = getActorsForCategory();
  const totalPages = getTotalPages();

  const getSelectedFilterDisplay = () => {
    if (isSearchActive) {
      return t('actorNamed').replace('{name}', searchTerm);
    }
    return tCategory(currentCategory === 'popular' ? 'popular' : currentCategory);
  };

  // ---- Handle media change inside content modal (update URL) ----
  const handleMediaChange = (media, mediaType) => {
    setSelectedMedia(media);
    setSelectedMediaType(mediaType);
    const actorId = searchParams.get('actor');
    if (actorId) {
      setContentParam(parseInt(actorId), media.id, mediaType);
    }
  };

  return (
    <div id="ActorsPage" className="page-content active">
      {loading && (
        <div className="loading-overlay">
          <img src="/loading2.png" alt="Loading..." className="blinking-logo" />
        </div>
      )}

      {!loading && (
        <div className="actors-container">
          <div>
            {filtersCollapsed ? (
              <div 
                className="collapsed-filters-btn"
                onClick={() => setFiltersCollapsed(false)}
              >
                <div className="filters-text">{t('filtersLabel')}</div>
                <div className="selected-filters">
                  {isSearchActive ? (
                    <>
                      <span>{t('actorNamed').replace('{name}', searchTerm)}</span>
                      <button 
                        className="clear-search-btn" 
                        onClick={(e) => {
                          e.stopPropagation();
                          clearSearch();
                        }}
                      >
                        &times;
                      </button>
                    </>
                  ) : (
                    getSelectedFilterDisplay()
                  )}
                </div>
              </div>
            ) : (
              <ActorsFilter
                currentCategory={currentCategory}
                onCategoryChange={handleCategoryChange}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onClearSearch={clearSearch}
                onSearchClick={handleSearchClick}
                loading={loading}
                inputRef={searchInputRef}
                lang={lang}
              />
            )}
          </div>
      
          <>
            {isSearchActive && actorsToDisplay.length === 0 && (
              <div className="no-results">
                {t('noResults').replace('{term}', searchTerm)}
              </div>
            )}
            
            <div className="actors-grid">
              {actorsToDisplay.map((actor) => (
                <div 
                  key={actor.id} 
                  className="actor-item"
                  onClick={() => handleActorSelect(actor)}
                >
                  <div className="actor-portrait">
                    <div className="portrait-container">
                      <img 
                        src={actor.imageUrl} 
                        alt={actor.name} 
                        className="portrait-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = "none";
                          e.target.parentNode.style.backgroundColor = "#e0e0e0";
                        }}
                      />
                    </div>
                  </div>
                  <div className="actor-name">{actor.name}</div>
                </div>
              ))}
            </div>
          </>
          
          {actorsToDisplay.length > 0 && totalPages > 0 && (
            <Pagination
              currentPage={isSearchActive ? 1 : currentPage}
              totalPages={isSearchActive ? 1 : totalPages}
              onPageChange={(page) => {
                if (!isSearchActive) {
                  setCurrentPage(page);
                  setFiltersCollapsed(true);
                }
              }}
              currentCategory={currentCategory}
              isSearchActive={isSearchActive}
              lang={lang}
            />
          )}
        </div>
      )}
      
      {selectedActor && (
        <ActorModal
          actor={selectedActor}
          onClose={handleActorModalClose}
          isMobile={isMobile}
          onContentClick={handleContentClick}
          history={history}
          predefinedBackdrops={predefinedBackdrops}
          getHighResImage={getHighResImage}
          actorFallbackImage={actorFallbackImage}
          TMDB_API_KEY={TMDB_API_KEY}
        />
      )}
      
      {/* Unified Media Modal */}
      {isContentModalOpen && selectedMedia && selectedMediaType && (
        <MediaModal
          media={selectedMedia}
          mediaType={selectedMediaType}
          onClose={handleContentModalClose}
          onMediaChange={handleMediaChange}
          onBack={handleBackFromContent}
          history={history}
        />
      )}
    </div>
  );
};

export default ActorsPage;